import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  Box,
  Button,
  Input,
  Modal,
  ModalBody,
  ModalClose,
  Spacer,
  Text,
} from '@flexisaf/flexibull2';
import Theme from 'utils/theme';
import request from 'utils/request';
import urls from 'utils/config';
import { currencyTags } from 'utils/constants';
import { formatNumber } from 'utils/formatNumber';

const { API_BASE_URL } = urls || {};

const Shell = styled.div`
  display: grid;
  grid-template-columns: 300px 1fr;
  min-height: 540px;
  border-radius: 18px;
  overflow: hidden;
  background: #fff;
  border: 1px solid ${Theme.PrimaryBorderColor};

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const Aside = styled.aside`
  background: ${Theme.PrimaryDark};
  color: #fff;
  padding: 36px 28px;
  display: flex;
  flex-direction: column;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    width: 180px;
    height: 180px;
    border-radius: 50%;
    right: -60px;
    bottom: -40px;
    background: ${Theme.ConditionalOfferStep};
    opacity: 0.35;
  }
`;

const Eyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  letter-spacing: 0.14em;
  font-size: 11px;
  font-weight: 700;
  color: #d7ffe4;
  text-transform: uppercase;
`;

const Dot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${Theme.PrimaryMint};
`;

const MetaLabel = styled.span`
  display: block;
  margin-top: 22px;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.55);
`;

const Sheet = styled.section`
  padding: 28px 32px 32px;
  display: flex;
  flex-direction: column;
`;

const OptionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
`;

const OptionCard = styled.button`
  text-align: left;
  border-radius: 14px;
  padding: 14px 16px;
  cursor: pointer;
  background: ${(props) => (props.$active ? '#f4fbf6' : '#fff')};
  border: 1.5px solid
    ${(props) => (props.$active ? Theme.ConditionalOfferStep : Theme.PrimaryBorderColor)};
  box-shadow: ${(props) =>
    props.$active ? '0 8px 20px rgba(86, 118, 93, 0.12)' : 'none'};

  &:disabled {
    cursor: default;
    opacity: 0.7;
  }
`;

const TotalRow = styled.div`
  margin-top: auto;
  padding-top: 18px;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  border-top: 1px solid ${Theme.PrimaryFade};
`;

const Line = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 8px 0;
  font-size: 13px;
  color: ${Theme.PrimaryGrey};
`;

const Notice = styled.div`
  background: #f7f4ff;
  border-radius: 12px;
  padding: 12px 14px;
  color: ${Theme.PrimaryDark};
  font-size: 13px;
`;

const CenterState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
`;

const money = (amount, currency) => {
  const symbol = currencyTags[currency] || '';
  return `${symbol}${formatNumber(Number(amount) || 0)}`;
};

const applicantName = (applicant) =>
  [applicant?.firstName, applicant?.middleName, applicant?.lastName]
    .filter(Boolean)
    .join(' ');

const AcceptanceFeeCheckout = ({
  open,
  applicant,
  organisationId,
  courseAlias,
  onClose,
  onPaid,
}) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState('');
  const [phone, setPhone] = useState('');
  const [completed, setCompleted] = useState(false);

  const loadSummary = async (paymentItemName) => {
    const payload = {
      organisation: organisationId,
      type: 'CONDITIONAL_OFFER',
      candidateId: applicant?.id,
    };
    if (paymentItemName) {
      payload.paymentItemName = paymentItemName;
    }
    const response = await request({
      method: 'post',
      url: `${API_BASE_URL}/checkout/summary`,
      data: payload,
    });
    if (!response.success) {
      throw new Error(response.message || 'Unable to load the acceptance fee');
    }
    return response.raw;
  };

  useEffect(() => {
    if (!open || !applicant?.id) return undefined;
    if (!organisationId) {
      setLoading(false);
      setError('Organisation is not loaded yet.');
      return undefined;
    }
    let cancelled = false;
    setCompleted(false);
    setError('');
    setSummary(null);
    setPhone(applicant.phoneNumber || applicant.phone || '');
    setLoading(true);
    loadSummary()
      .then((raw) => {
        if (cancelled || !raw) return;
        const next = { ...raw };
        if ((next.paymentOptions || []).length > 1) {
          next.selectedPaymentItemName = '';
        }
        setSummary(next);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, applicant?.id, organisationId]);

  const selectOption = async (paymentItemName) => {
    if (!paymentItemName || paymentItemName === summary?.selectedPaymentItemName) {
      return;
    }
    setSelecting(true);
    setError('');
    try {
      const raw = await loadSummary(paymentItemName);
      setSummary(raw);
    } catch (err) {
      setError(err.message);
    } finally {
      setSelecting(false);
    }
  };

  const pay = async () => {
    if (!phone.trim()) {
      setError("Enter the payer's phone number to continue.");
      return;
    }
    const options = summary?.paymentOptions || [];
    if (options.length > 1 && !summary?.selectedPaymentItemName) {
      setError('Choose a payment option to continue.');
      return;
    }
    setPaying(true);
    setError('');
    const response = await request({
      method: 'post',
      url: `${API_BASE_URL}/candidates/${applicant.id}/conditional-offer/checkout`,
      data: {
        phone: phone.trim(),
        paymentItemName: summary.selectedPaymentItemName,
      },
    });
    if (!response.success) {
      setPaying(false);
      setError(response.message || 'Unable to start the payment.');
      return;
    }
    if (response.raw?.status === 'SUCCESS') {
      setPaying(false);
      setCompleted(true);
      if (onPaid) onPaid();
      return;
    }
    if (response.raw?.status === 'PAYMENT_PENDING' && response.raw?.redirectUrl) {
      window.location.assign(response.raw.redirectUrl);
      return;
    }
    setPaying(false);
    setError('The payment gateway did not return a checkout link.');
  };

  const currency =
    summary?.items?.[0]?.currency || summary?.paymentOptions?.[0]?.currency || 'NGN';
  const paid = summary?.hasCompletedPayment;
  const options = summary?.paymentOptions || [];

  return (
    <Modal open={open} onClose={onClose} outerclick center>
      <ModalBody width="920px" style={{ maxWidth: '94vw' }}>
        <ModalClose onClick={onClose}>&times;</ModalClose>
        <Shell>
          <Aside>
            <Eyebrow>
              <Dot />
              Acceptance fee
            </Eyebrow>
            <Spacer space="18px" />
            <Text bold size="26px" color="#fff" block style={{ lineHeight: 1.2, position: 'relative' }}>
              {applicantName(applicant) || 'Applicant'}
            </Text>
            <MetaLabel>Application number</MetaLabel>
            <Text bold color="#fff" block style={{ position: 'relative' }}>
              {applicant?.applicationNumber || '—'}
            </Text>
            <MetaLabel>{courseAlias || 'Course'}</MetaLabel>
            <Text color="#fff" block style={{ position: 'relative' }}>
              {applicant?.course?.title || '—'}
            </Text>
            {applicant?.course?.code ? (
              <Text size="12px" color="rgba(255,255,255,0.7)" block style={{ position: 'relative' }}>
                {applicant.course.code}
              </Text>
            ) : null}
            <Box style={{ marginTop: 'auto', position: 'relative' }}>
              <Text size="12px" color="rgba(255,255,255,0.65)">
                Payment is collected through the institution gateway and recorded on this application.
              </Text>
            </Box>
          </Aside>
          <Sheet>
            <Text bold size="20px" color={Theme.PrimaryDark} block>
              Pay acceptance fee
            </Text>
            <Spacer space="6px" />
            <Text size="13px" color={Theme.PrimaryGrey}>
              {summary?.applicationTitle || applicant?.applicationTitle || 'Conditional offer'}
            </Text>
            <Spacer space="20px" />

            {loading ? (
              <CenterState>
                <Text color={Theme.PrimaryGrey}>Loading the fee…</Text>
              </CenterState>
            ) : null}

            {!loading && error && !summary ? (
              <CenterState>
                <Text bold block color={Theme.PrimaryRed}>
                  {error}
                </Text>
                <Spacer space="16px" />
                <Button pale onClick={onClose}>
                  Close
                </Button>
              </CenterState>
            ) : null}

            {!loading && completed ? (
              <CenterState>
                <Eyebrow style={{ color: Theme.ConditionalOfferStep }}>
                  <Dot />
                  Paid
                </Eyebrow>
                <Spacer space="12px" />
                <Text bold size="28px" color={Theme.PrimaryDark} block>
                  Acceptance fee recorded
                </Text>
                <Spacer space="8px" />
                <Text color={Theme.PrimaryGrey}>
                  No gateway charge was required. The applicant can continue from here.
                </Text>
                <Spacer space="22px" />
                <Button color={Theme.ConditionalOfferStep} onClick={onClose}>
                  Done
                </Button>
              </CenterState>
            ) : null}

            {!loading && summary && paid && !completed ? (
              <CenterState>
                <Text bold size="28px" color={Theme.PrimaryDark} block>
                  Already paid
                </Text>
                <Spacer space="8px" />
                <Text color={Theme.PrimaryGrey}>
                  A successful payment is already recorded for this fee.
                </Text>
                <Spacer space="22px" />
                <Button pale onClick={onClose}>
                  Close
                </Button>
              </CenterState>
            ) : null}

            {!loading && summary && !paid && !completed ? (
              <>
                {options.length > 1 ? (
                  <>
                    <Text bold size="12px" color={Theme.PrimaryGrey} uppercase>
                      Payment option
                    </Text>
                    <Spacer space="10px" />
                    <OptionGrid>
                      {options.map((option) => (
                        <OptionCard
                          key={option.name}
                          type="button"
                          $active={option.name === summary.selectedPaymentItemName}
                          disabled={selecting || paying}
                          onClick={() => selectOption(option.name)}
                        >
                          <Text size="11px" bold uppercase color={Theme.ConditionalOfferStep} block>
                            {option.tag || 'Fee'}
                          </Text>
                          <Spacer space="6px" />
                          <Text bold block color={Theme.PrimaryDark}>
                            {option.name}
                          </Text>
                          <Spacer space="8px" />
                          <Text bold size="18px" color={Theme.PrimaryDark}>
                            {money(option.amount, option.currency)}
                          </Text>
                        </OptionCard>
                      ))}
                    </OptionGrid>
                    <Spacer space="18px" />
                  </>
                ) : null}

                {options.length > 1 && !summary.selectedPaymentItemName ? (
                  <Text size="13px" color={Theme.PrimaryGrey}>
                    Choose the fee to charge. The acceptance letter follows this option.
                  </Text>
                ) : (
                  (summary.items || []).map((item) => (
                    <Line key={`${item.name}-${item.amount}`}>
                      <span>{item.name}</span>
                      <span>{money(item.amount, item.currency || currency)}</span>
                    </Line>
                  ))
                )}
                {summary.paymentGatewayFee > 0 ? (
                  <Line>
                    <span>Gateway fee</span>
                    <span>{money(summary.paymentGatewayFee, currency)}</span>
                  </Line>
                ) : null}

                {summary.hasPendingPayment ? (
                  <>
                    <Spacer space="8px" />
                    <Notice>
                      A payment for this fee is still pending. Starting checkout opens a new gateway session.
                    </Notice>
                  </>
                ) : null}

                <Spacer space="16px" />
                <Input
                  label="Payer phone"
                  name="acceptance-fee-phone"
                  placeholder="Phone number"
                  value={phone}
                  onChange={(event) => setPhone(event.target.value)}
                  block
                  spaceTop
                />

                {error ? (
                  <>
                    <Spacer space="10px" />
                    <Text size="13px" color={Theme.PrimaryRed} block>
                      {error}
                    </Text>
                  </>
                ) : null}

                <TotalRow>
                  <div>
                    <Text size="11px" bold uppercase color={Theme.PrimaryGrey} block>
                      Amount due
                    </Text>
                    <Text bold size="32px" color={Theme.PrimaryDark}>
                      {options.length > 1 && !summary.selectedPaymentItemName
                        ? '—'
                        : money(summary.totalAmount, currency)}
                    </Text>
                    {summary.paymentConfiguration?.paymentPlatform ? (
                      <Text size="12px" color={Theme.PrimaryGrey} block>
                        via {summary.paymentConfiguration.paymentPlatform}
                      </Text>
                    ) : null}
                  </div>
                  <Button
                    color={Theme.PrimaryColor}
                    progress={paying || selecting}
                    disabled={paying || selecting || (options.length > 1 && !summary.selectedPaymentItemName)}
                    onClick={pay}
                    height={48}
                    style={{ minWidth: 180 }}
                  >
                    Pay now
                  </Button>
                </TotalRow>
              </>
            ) : null}
          </Sheet>
        </Shell>
      </ModalBody>
    </Modal>
  );
};

AcceptanceFeeCheckout.propTypes = {
  open: PropTypes.bool,
  applicant: PropTypes.shape({
    id: PropTypes.string,
    firstName: PropTypes.string,
    middleName: PropTypes.string,
    lastName: PropTypes.string,
    applicationNumber: PropTypes.string,
    applicationTitle: PropTypes.string,
    phoneNumber: PropTypes.string,
    phone: PropTypes.string,
    course: PropTypes.shape({
      title: PropTypes.string,
      code: PropTypes.string,
    }),
  }),
  organisationId: PropTypes.string,
  courseAlias: PropTypes.string,
  onClose: PropTypes.func,
  onPaid: PropTypes.func,
};

export default AcceptanceFeeCheckout;
